import { toast } from "react-toastify";
import { accessErrorCode } from "@src/utils/teamAccess";

const BASE_URL = process.env.REACT_APP_API_URL ?? window.location.origin;

type QueryParams = Record<string, string | number | boolean | undefined | null>;

/**
 * Helper to build query string from object, filtering out null/undefined values
 */
const buildQueryString = (params?: QueryParams): string => {
  if (!params) return "";
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
};

interface RequestOptions extends RequestInit {
  params?: QueryParams;
  skipAuth?: boolean;
  /**
   * Suppress the automatic error toast. For calls whose failure is a normal,
   * expected state that the UI already renders — feature probes, capability
   * checks, background polls. The error still rejects, so callers keep full
   * control; only the toast is skipped.
   *
   * The case that forced this: a feature-flagged endpoint answers 404 to users
   * outside the flag, and a status probe would pop "Not found" at them for
   * simply opening a menu.
   */
  silent?: boolean;
}

// Backend "one at a time per user" guards return 409 with one of these stable
// codes as the message. They are not real errors — the caller shows a localized
// "please wait" panel — so the generic error toast is suppressed for them and
// the code is surfaced on `error.code` instead.
const BUSY_CODES = new Set(["PRESCRIPTION_IN_PROGRESS", "PRODUCTIVITY_IN_PROGRESS"]);

/** The one error shape every API call rejects with. */
export type ApiError = Error & {
  statusCode: number;
  termsLocation?: string;
  connectionsUrl?: string;
  /** Stable machine-readable reason (e.g. DUPLICATE_FIELD_GEOMETRY, or a driver-access code). */
  errorCode?: string;
  /** "One at a time" busy code (PRESCRIPTION_IN_PROGRESS, PRODUCTIVITY_IN_PROGRESS). */
  code?: string;
  /** Set when the toast is not wanted: the caller shows a link, a busy panel, or the session is gone. */
  quiet?: boolean;
};

/**
 * Turn a non-ok Response into an ApiError. Never toasts — `request()` decides
 * that; raw-fetch services call this directly so every failure reads the same.
 *
 * The backend replies `{ error, code, errorCode? }`. Raw-fetch services used to
 * read `.message`, which it never sends, so the user saw a generic fallback (or
 * "[object Object]" when `error` was a raw ZodError). Only a STRING is taken as
 * the message; `fallback` is used when there is none.
 */
export async function toApiError(response: Response, fallback?: string): Promise<ApiError> {
  let body: Record<string, unknown> = {};
  try {
    body = await response.json();
  } catch {
    // Non-JSON error body — status text / fallback below.
  }
  const text = [body.message, body.error].find(
    (v): v is string => typeof v === "string" && v.length > 0
  );
  const error = new Error(
    text ?? fallback ?? (response.statusText || `Request failed with status ${response.status}`)
  ) as ApiError;
  error.statusCode = response.status;
  if (typeof body.termsLocation === "string") error.termsLocation = body.termsLocation;
  if (typeof body.connectionsUrl === "string") error.connectionsUrl = body.connectionsUrl;
  // `error.message` carries the human text and moves; callers that branch on
  // the reason read this instead.
  if (typeof body.errorCode === "string") error.errorCode = body.errorCode;
  // Driver-access refusals (role without fields, or a "Driver app only" role
  // used from the web). The UI hides those areas and the interceptor falls back
  // to the user's own account, so a stray call must not raise a toast.
  const accessCode = response.status === 403 ? accessErrorCode(body) : null;
  if (accessCode) error.errorCode = accessCode;
  if (text && BUSY_CODES.has(text)) error.code = text;
  // A caller-rendered link, a localized busy panel, a dead session (the auth
  // interceptor signs out; one dashboard load used to stack six identical
  // "Unauthorized" toasts) or an access refusal: no toast.
  error.quiet = Boolean(
    error.termsLocation || error.connectionsUrl || error.code || accessCode || response.status === 401
  );
  return error;
}

/**
 * Unified API client to handle common fetch logic
 */
async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, skipAuth, silent, headers, ...customConfig } = options;
  
  const url = `${BASE_URL}${endpoint}${buildQueryString(params)}`;
  
  const isFormData = customConfig.body instanceof FormData;
  const config: RequestInit = {
    ...customConfig,
    headers: isFormData
      ? { ...headers }
      : {
          "Content-Type": "application/json",
          ...headers,
        },
  };

  // If using authClient from better-auth, tokens are usually handled automatically 
  // or via headers. The existing code uses an interceptor (apiAuthInterceptor),
  // which monkey-patches window.fetch. Since we use window.fetch here, it should work.
  // However, we can also explicitly get session if needed.

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const error = await toApiError(response);
      if (!silent && !error.quiet) toast.error(error.message);
      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  } catch (error) {
    // Re-throw to let caller handle specific cases if needed, 
    // or add global error logging here (e.g. Sentry)
    throw error;
  }
}

export const api = {
  get: <T>(endpoint: string, params?: QueryParams, options?: Omit<RequestOptions, 'params' | 'method'>) => 
    request<T>(endpoint, { ...options, method: 'GET', params }),

  post: <T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'body' | 'method'>) => 
    request<T>(endpoint, { ...options, method: 'POST', body: JSON.stringify(body) }),

  put: <T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'body' | 'method'>) => 
    request<T>(endpoint, { ...options, method: 'PUT', body: JSON.stringify(body) }),

  patch: <T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'body' | 'method'>) => 
    request<T>(endpoint, { ...options, method: 'PATCH', body: JSON.stringify(body) }),

  delete: <T>(endpoint: string, params?: QueryParams, options?: Omit<RequestOptions, 'params' | 'method'>) => 
    request<T>(endpoint, { ...options, method: 'DELETE', params }),
    
  /**
   * Binary GET. `request` always parses the body as JSON, which a file download
   * cannot be — but a FAILED download still answers with the usual JSON error
   * body, so the error path is kept in step with the rest of the client.
   */
  getBlob: async (endpoint: string, params?: QueryParams): Promise<Blob> => {
    const url = `${BASE_URL}${endpoint}${buildQueryString(params)}`;
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) throw await toApiError(response);
    return response.blob();
  },

  /** Binary POST with a JSON body (e.g. a multi-layer prescription export). */
  postBlob: async (endpoint: string, body: unknown): Promise<Blob> => {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw await toApiError(response);
    return response.blob();
  },

  // For file uploads which require FormData and no Content-Type header (let browser set it)
  upload: <T>(endpoint: string, file: File, fieldName = "file", params?: QueryParams) => {
    const formData = new FormData();
    formData.append(fieldName, file);
    
    // We explicitly remove Content-Type so browser sets boundary
    return request<T>(endpoint, {
      method: 'POST',
      body: formData,
      params,
      headers: {} // Overwrite default Content-Type
    } as any); // Cast because we manipulate headers loosely here
  }
};
